import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { ReactiveJsonRoot } from '@ea-lab/reactive-json';
import { AlertTriangle, Archive, Menu } from 'lucide-react';
import { DocFiltersProvider } from '../DocFiltersContext';
import DocArchivedContext from '../DocArchivedContext';
import DocAccessDenied from '../DocAccessDenied';
import DocSidebarShell, { readCollapsed, writeCollapsed } from './DocSidebarShell';
import DocSidebarTree from './DocSidebarTree';
import DocSettingsSidebarBody from '../settings/DocSettingsSidebarBody';
import DocTrashList from '../trash/DocTrashList';

function formatArchiveDate(isoStr) {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

async function fetchDirectoryMeta(id) {
  const res = await fetch(
    `/decoupled-api/taxonomy_term/doc_directory/${id}?display=decoupled`,
    { credentials: 'include' },
  );
  if (!res.ok) return null;
  const json = await res.json();
  const d = json?.data ?? json;
  return {
    id: d.id,
    name: d.name || '',
    icon: d.icon || '',
    parentId: d.parentId ? parseInt(d.parentId, 10) : 0,
  };
}

async function resolveRootSpace(dirId) {
  let current = await fetchDirectoryMeta(dirId);
  while (current && current.parentId) {
    current = await fetchDirectoryMeta(current.parentId);
  }
  return current;
}

export default function DocShell({ rjbuild, plugins }) {
  const params = useParams();
  const location = useLocation();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [wideMode, setWideMode] = useState(() => {
    try { return localStorage.getItem('orbit-doc-wide-mode') === '1'; } catch { return false; }
  });

  const searchRef = useRef(null);
  const treeCreateRef = useRef(null);
  const mainRef = useRef(null);

  const [refreshKey, setRefreshKey] = useState(0);

  const searchParams = new URLSearchParams(location.search);
  const id = params.id || searchParams.get('id') || '';
  const isSettings = location.pathname === '/docs/settings';
  const isTrash = location.pathname === '/docs/trash';
  const isHub = !id && !isSettings && !isTrash;
  const isSpace = location.pathname.startsWith('/docs/space/');
  const isPage = location.pathname.startsWith('/docs/page/');
  // Lu UNE fois, au montage : le paramètre est retiré de l'URL juste après
  // (cf. l'effet plus bas), pour qu'un rafraîchissement ultérieur ne rouvre pas
  // l'édition — même règle que le `?edit=1` du builder.
  const editOnOpen = searchParams.get('edit') === '1';

  const [archivedAt, setArchivedAt] = useState(null);
  const [currentSpace, setCurrentSpace] = useState(null);
  const [spaceResolved, setSpaceResolved] = useState(!isSpace && !isPage);
  const [accessDenied, setAccessDenied] = useState(false);

  /* ── Resolve current space from route ── */
  useEffect(() => {
    if (!id || (!isSpace && !isPage)) {
      setCurrentSpace(null);
      setSpaceResolved(true);
      setAccessDenied(false);
      return;
    }
    setSpaceResolved(false);
    setAccessDenied(false);
    let cancelled = false;
    (async () => {
      try {
        if (isSpace) {
          const meta = await fetchDirectoryMeta(id);
          if (!cancelled && meta) {
            if (meta.parentId === 0) {
              setCurrentSpace(meta);
            } else {
              const root = await resolveRootSpace(meta.parentId);
              if (!cancelled && root) setCurrentSpace(root);
            }
          }
        } else {
          const pageRes = await fetch(
            `/decoupled-api/node/builder_page/${id}?display=decoupled`,
            { credentials: 'include' },
          );
          if (cancelled) return;
          if (pageRes.status === 403) {
            setAccessDenied(true);
            return;
          }
          if (!pageRes.ok) return;
          const pageJson = await pageRes.json();
          const dirId = parseInt((pageJson?.data ?? pageJson)?.directoryId, 10);
          if (!Number.isFinite(dirId)) return;
          const root = await resolveRootSpace(dirId);
          if (!cancelled && root) setCurrentSpace(root);
        }
      } catch { /* network error — sidebar falls back to hub mode */ }
      finally { if (!cancelled) setSpaceResolved(true); }
    })();
    return () => { cancelled = true; };
  }, [id, isSpace, isPage]);

  useEffect(() => {
    const handler = () => setRefreshKey((k) => k + 1);
    window.addEventListener('doc:entity-trashed', handler);
    window.addEventListener('doc:entity-archived', handler);
    return () => {
      window.removeEventListener('doc:entity-trashed', handler);
      window.removeEventListener('doc:entity-archived', handler);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(rjbuild, { credentials: 'include' });
        if (!res.ok) throw new Error(`Failed to fetch ${rjbuild} (${res.status})`);
        let text = await res.text();
        if (id) text = text.replace(/__URL_ID__/g, id);
        // `?edit=1` → l'écran s'ouvre en ÉDITION. C'est ce que passent les
        // écrans de création d'une page : on vient d'en faire une, on veut
        // l'écrire, pas la contempler vide. Substitué dans le TEXTE du rjBuild
        // comme `__URL_ID__` — le YAML n'a aucun accès à la query string.
        text = text.replace(/__EDIT_MODE__/g, editOnOpen ? 'true' : 'false');
        if (!cancelled) setContent(text);
      } catch (err) {
        console.error('DocShell load error:', err);
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  // `editOnOpen` est volontairement HORS des dépendances : il n'est lu qu'au
  // chargement du rjBuild, et le retirer de l'URL ci-dessous ne doit pas
  // relancer ce chargement (ce qui remonterait l'écran en lecture).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rjbuild, id]);

  // Nettoyer `?edit=1` une fois consommé : sans cela, un rafraîchissement — ou
  // un retour arrière — rouvrirait l'édition alors que l'éditeur en est sorti.
  useEffect(() => {
    if (!editOnOpen) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('edit');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
  }, [editOnOpen]);

  useEffect(() => {
    setSidebarOpen(false);
    setArchivedAt(null);
    setAccessDenied(false);
  }, [location.pathname]);

  const handleCollapse = useCallback(() => {
    setCollapsed(true);
    writeCollapsed(true);
  }, []);

  const handleExpand = useCallback(() => {
    setCollapsed(false);
    writeCollapsed(false);
  }, []);

  /* ── Check if current space or page is archived ── */
  useEffect(() => {
    if (!id || (!isSpace && !isPage)) return;
    let cancelled = false;
    const entityType = isSpace ? 'taxonomy_term/doc_directory' : 'node/builder_page';
    fetch(`/decoupled-api/${entityType}/${id}?display=decoupled&include_archived=1`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (cancelled || !data) return;
        const archived = data?.data?.archived || data?.archived || null;
        setArchivedAt(archived || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [id, isSpace, isPage]);

  const handleTreeCreateSpace = useCallback(() => {
    treeCreateRef.current?.();
  }, []);

  const toggleWideMode = useCallback(() => {
    setWideMode((prev) => {
      const next = !prev;
      try { localStorage.setItem('orbit-doc-wide-mode', next ? '1' : '0'); } catch {}
      return next;
    });
  }, []);

  useEffect(() => {
    const syncWide = () => {
      try { setWideMode(localStorage.getItem('orbit-doc-wide-mode') === '1'); } catch {}
    };
    window.addEventListener('doc:toggle-wide-mode', syncWide);
    return () => window.removeEventListener('doc:toggle-wide-mode', syncWide);
  }, []);

  return (
    <DocFiltersProvider>
      <DocArchivedContext.Provider value={archivedAt}>
      <div id="doc-shell" className="h-screen bg-stone-50 flex flex-col font-sans overflow-hidden">
        <ReactiveJsonRoot
          rjBuildUrl="/components/layout/Navigation.yaml"
          rjBuildFetchMethod="GET"
          plugins={plugins}
        />

        <div id="doc-shell-body" className="flex flex-1 overflow-hidden">
          {/* Le hub (/docs) est une PAGE DE LISTE pleine largeur : plus de
              colonne de gauche. Ses filtres sont dans la page et ses raccourcis
              (corbeille, configuration, groupes) dans son en-tête. La colonne
              reste sur un espace ou une page, où elle porte l'arborescence. */}
          {!isHub && (
          <DocSidebarShell
            isSettings={isSettings}
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            onCreateSpace={handleTreeCreateSpace}
            currentSpace={currentSpace}
            collapsed={collapsed}
            onCollapse={handleCollapse}
            onExpand={handleExpand}
            isPage={isPage}
          >
            {isSettings ? (
              <DocSettingsSidebarBody />
            ) : !spaceResolved ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-stone-200 border-t-blue-500" />
              </div>
            ) : (
              <DocSidebarTree searchRef={searchRef} currentSpaceId={currentSpace?.id ?? null} />
            )}
          </DocSidebarShell>
          )}

          {/* Plus de seconde colonne sur une page : l'écran ne porte que
              l'arborescence et LA PAGE. Le panneau contextuel (sommaire,
              tâches, commentaires, historique) montait ici, en vis-à-vis du
              contenu — il a été retiré avec le reste du chrome de page. */}

          <main ref={mainRef} id="doc-shell-main" className="group flex-1 overflow-y-auto bg-white" role="main" data-archived={archivedAt ? 'true' : undefined} data-wide-mode={wideMode ? '' : undefined}>
            {/* Hamburger mobile — sans objet sur le hub, qui n'a plus de colonne. */}
            <div id="doc-shell-mobile-bar" className={`${isHub ? 'hidden' : 'md:hidden'} sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-stone-200 px-4 py-2.5`}>
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-500 transition-colors"
                aria-label="Ouvrir le menu de navigation"
              >
                <Menu size={20} />
              </button>
            </div>

            {archivedAt && (
              <div className="sticky top-0 z-20 flex items-center gap-3 px-6 py-3 bg-amber-50 border-b border-amber-200 text-amber-800 text-sm" role="status">
                <Archive size={16} className="flex-shrink-0 text-amber-500" />
                <span>
                  <strong>Contenu archivé</strong> — Archivé le {formatArchiveDate(archivedAt)}. Ce contenu est en <strong>lecture seule</strong>.
                </span>
              </div>
            )}

            {accessDenied && isPage ? (
              <DocAccessDenied pageId={id} />
            ) : isTrash ? (
              <DocTrashList />
            ) : loading ? (
              <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-stone-200 border-t-blue-600" />
                <span className="text-sm text-stone-400">Chargement...</span>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 px-6">
                <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center">
                  <AlertTriangle size={24} className="text-amber-500" />
                </div>
                <p className="text-stone-700 font-medium">Impossible de charger la page</p>
                <p className="text-sm text-stone-400 text-center max-w-md">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-1 px-5 py-2.5 bg-stone-900 text-white rounded-xl text-sm font-medium hover:bg-stone-800 transition-colors"
                >
                  Réessayer
                </button>
              </div>
            ) : !content ? (
              <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3">
                <p className="text-stone-400 text-sm">Aucun contenu disponible.</p>
              </div>
            ) : (
              <ReactiveJsonRoot
                key={`${rjbuild}-${id}-${refreshKey}`}
                maybeRawAppRjBuild={content}
                rjBuildFetchMethod="GET"
                plugins={plugins}
              />
            )}

          </main>
        </div>
      </div>
      </DocArchivedContext.Provider>
    </DocFiltersProvider>
  );
}
